import {
  GetOptionFlowQuery,
  GetFreeOptionFlowQuery,
  GetTickerOptionFlowQuery,
  GetFullFeedTickerOptionFlowQuery,
  GetHistoricalOptionFlowQuery,
  GetHistoricalOptionFlowWithTickersQuery,
  GetTopTickersQuery,
  GetDarkPoolTradesBaselineQuery,
  GetDarkPoolTradesFullQuery,
  GetDarkPoolTradesQuery,
  GetHistoricalDarkPoolBaselineQuery,
  GetHistoricalDarkPoolWithTickersBaselineQuery,
  GetFreeTickerOptionFlowQuery,
  GetWeeklyOverviewQuery,
} from 'types/generated/client';
import type { FlowMetricMode, FlowSideBasisSelection, PutFlowBasis } from './presets';
import type { DelayedFlowWait } from 'components/DelayedFlowNotice/delayedFlowNoticeModel';

export const HIGH_VOLUME_TRADE_PERCENT = 0.3;

export enum MobileLayout {
  Cards = 'CARDS',
  Table = 'TABLE',
}

export enum Sentiment {
  Bullish = 'BULLISH',
  Bearish = 'BEARISH',
  None = 'NONE',
}

export enum TradeDirection {
  Ask = 'A',
  AboveAsk = 'AA',
  Mid = 'M',
  Bid = 'B',
  BelowBid = 'BB',
}

interface DerivedTradeValues {
  heatScore: number;
  scoreWidth: number;
  time: string;
  timeSubscript: string;
  expiry?: number;
  expiryString: string;
  daysToExpiry: number;
  isUnusual: boolean;
  isTopPosition: boolean;
  isGoldenSweep: boolean;
  tradeDirection?: TradeDirection;
  createdTimestamp: number;
  /** Execution time (last print) in ms; createdAt/createdTimestamp retain ingestion time. */
  tradeTimestamp?: number;
}

export type OptionFlowTrade = GetOptionFlowQuery['option_flow'][0] & DerivedTradeValues;
export type TickerFlowTrade = GetTickerOptionFlowQuery['ticker_option_flow'][0] &
  DerivedTradeValues;
export type TopTickersFlowTrade = GetTopTickersQuery['top_tickers'][0]['firstTrades'][0] &
  DerivedTradeValues;
export type HistoricalOptionFlow = GetHistoricalOptionFlowQuery['option_feed_filtered'][0] &
  DerivedTradeValues;
export type HistoricalOptionFlowWithTickers =
  GetHistoricalOptionFlowWithTickersQuery['option_feed_filtered'][0] & DerivedTradeValues;

export type DailyVolume = GetOptionFlowQuery['daily_volume'][0];
export type RawTrade =
  | GetOptionFlowQuery['option_flow'][0]
  | GetFreeOptionFlowQuery['free_option_flow'][0]
  | GetTickerOptionFlowQuery['ticker_option_flow'][0]
  | GetFreeTickerOptionFlowQuery['free_ticker_option_flow'][0]
  | GetWeeklyOverviewQuery['weekly_option_flow'][0]
  | GetFullFeedTickerOptionFlowQuery['full_feed_option_flow'][0]
  | GetHistoricalOptionFlowQuery['option_feed_filtered'][0]
  | GetHistoricalOptionFlowWithTickersQuery['option_feed_filtered'][0]
  | GetTopTickersQuery['top_tickers'][0]['firstTrades'][0];
export type CombinedTrade =
  | OptionFlowTrade
  | TickerFlowTrade
  | HistoricalOptionFlow
  | HistoricalOptionFlowWithTickers
  | TopTickersFlowTrade;

/**
 * Higher-order greeks and quote-model inputs produced by the realtime options
 * enrichment pipeline. The GraphQL views expose these fields after the matching
 * Hasura migration is applied; keeping them optional preserves compatibility
 * with older historical rows and schemas during the rollout.
 */
export interface OptionFlowEnrichmentFields {
  vanna?: number | null;
  vomma?: number | null;
  charm?: number | null;
  zomma?: number | null;
  color?: number | null;
  speed?: number | null;
  veta?: number | null;
  vera?: number | null;
  ultima?: number | null;
  lambda?: number | null;
  probTouch?: number | null;
  riskFreeRate?: number | null;
  nbboMidIv?: number | null;
}

export type Trade = Omit<CombinedTrade, '__typename'>;

export enum CallPut {
  Call = 'Call',
  Put = 'Put',
}

export enum TradeSide {
  AboveAsk = 'ABOVE_ASK',
  Bid = 'BID',
  Ask = 'ASK',
  Mid = 'MID',
  BelowBid = 'BELOW_BID',
  None = 'NONE',
}

export enum OrderType {
  Block = 'BLOCK',
  Sweep = 'SWEEP',
  Split = 'SPLIT',
}

export const DAYS_PER_YEAR = 365;

export enum SortDirection {
  Asc = 'ASC',
  Desc = 'DESC',
  None = 'NONE',
}

export enum ColumnType {
  Text = 'TEXT',
  Number = 'NUMBER',
  Date = 'DATE',
}

export interface FlowTableHeaderProps {
  flowSortKey: string;
  setFlowSortKey: (key: string) => void;
  flowSortDirection: SortDirection;
  setFlowSortDirection: (direction: SortDirection) => void;
  isWatchListOnly: boolean;
  setIsWatchListOnly: (isWatchListOnly: boolean) => void;
  hasWatchListItems: boolean;
  isSoundEnabled: boolean;
  setIsSoundEnabled: (isSoundEnabled: boolean) => void;
  mobileLayout?: MobileLayout;
  setMobileLayout?: (mobileLayout: MobileLayout) => void;
  data?: FullTrade[];
  hasAccess: boolean;
  isUserSession?: boolean;
  setIsWatchListModalOpen?: (isOpen: boolean) => void;
  isEarningsOnly?: boolean;
  setIsEarningsOnly?: (value: boolean) => void;
  isShowcase?: boolean;
  flowSideBasis?: FlowSideBasisSelection;
  flowSideLabel?: string;
  putFlowBasis?: PutFlowBasis;
  metricMode?: FlowMetricMode;
}

interface FlowDetails {
  heatScore: number;
  scoreWidth: number;
  time: string;
  timeSubscript: string;
  expiry?: number;
  expiryString: string;
  daysToExpiry: number;
}

export type FullTrade = Trade & FlowDetails & { isBlur?: boolean };
export interface FlowTableRowProps {
  trade: FullTrade;
  watchListTickers: string[];
  trackRowAsAnimated?: () => void;
  hasBeenAnimated?: boolean;
  handleBookmark?: (ticker: string) => void;
  earningsMap?: Record<string, any>;
  tradesByTicker?: any[];
}

export interface FlowTableProps extends FlowTableHeaderProps {
  trades: FullTrade[];
  // Unfiltered trades passed straight through for the GRAPH view's SPX
  // reference line. Lets SPX stay visible even when the table preset
  // (EQUITIES, ETFs, etc.) filters indices out of `trades`.
  referenceTrades?: FullTrade[];
  watchListTickers: string[];
  totalFlowCount?: number;
  isUserSession?: boolean;
  isEarningsOnly?: boolean;
  setIsEarningsOnly?: (value: boolean) => void;
  setIsWatchListModalOpen?: (isOpen: boolean) => void;
  handleBookmark?: (symbol: string) => void;
  earningsMap?: Record<string, any>;
  tradesByTicker?: any[];
  isShowcase?: boolean;
  defaultMobileLayout?: MobileLayout;
  onOpenPopover?: (trade: FullTrade, anchorElement?: HTMLElement) => void;
  isMobile?: boolean;
  // Set while the viewer's delayed (free) feed is connected but holds no trades yet. The table
  // explains the 30-minute delay instead of rendering an empty body.
  delayedFlowWait?: DelayedFlowWait | null;
}

export enum TopTickerKeys {
  UnusualOTM = 'UNUSUAL_OTM',
  Momentum = 'MOMENTUM',
  SweepTrades = 'SWEEP_TRADES',
  ImpliedVol = 'IMPLIED_VOl',
  TopPositions = 'TOP_POSITIONS',
}

export enum SignaturePrintStatus {
  None = 'NONE',
  Buy = 'BUY',
  Sell = 'SELL',
}

export interface EquityPrintOverview {
  open: number;
  high: number;
  low: number;
  lastPrice: number;
  avgPrice: number;
}

export type EquityPrint = (
  | GetDarkPoolTradesQuery['equity_print_flow'][number]
  | GetDarkPoolTradesBaselineQuery['equity_print_feed_baseline'][number]
  | GetDarkPoolTradesFullQuery['equity_print_flow_full'][number]
  | GetHistoricalDarkPoolBaselineQuery['equity_print_feed_baseline'][number]
  | GetHistoricalDarkPoolWithTickersBaselineQuery['equity_print_feed_baseline'][number]
) & {
  tickerDetails?: {
    name?: string | null;
    ticker?: string | null;
    sector?: string | null;
  } | null;
};
